import Modal from '../ui/Modal'
import Button from '../ui/Button'
import Spinner from '../ui/Spinner'
import type { PreviewResponse } from '../../types'

interface PreviewModalProps {
  isOpen: boolean
  /** True while the preview request is in flight. */
  loading: boolean
  /** Error message when the preview endpoint failed (null otherwise). */
  error: string | null
  /** The plan returned by ``POST /jobs/preview`` (null while loading/failed). */
  preview: PreviewResponse | null
  /** "Confirmar y generar" — create the real job(s). */
  onConfirm: () => void
  /** "Volver" — close the modal and let the user edit. */
  onBack: () => void
  /** "Generar sin resumen" — generate despite the preview failure. */
  onGenerateAnyway: () => void
  /** True while the real job is being created (disables the buttons). */
  creating: boolean
}

/** Severity → badge style for plan warnings. */
const SEVERITY_CLASS: Record<string, string> = {
  warning: 'bg-amber-50 border-amber-200 text-amber-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
}

/** Severity → icon. */
const SEVERITY_ICON: Record<string, string> = {
  warning: '⚠️',
  info: 'ℹ️',
}

/**
 * PreviewModal — the pre-generation summary ("resumen previo").
 *
 * Shown when the user clicks "Generar PDF": it asks the backend to PLAN the
 * job(s) first (solo lists vs ``_union`` merges) and displays, per job, the
 * card count, blank slots and front/back pages, plus plan-wide warnings and
 * totals. Only "Confirmar y generar" creates the real job.
 *
 * UX decision (agreed with the coordinator): if the preview endpoint fails,
 * we DON'T block generation — the modal shows the error and offers an
 * explicit "Generar sin resumen" button, since generation is the critical
 * action and the preview is informative.
 */
export default function PreviewModal({
  isOpen,
  loading,
  error,
  preview,
  onConfirm,
  onBack,
  onGenerateAnyway,
  creating,
}: PreviewModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onBack} title="Resumen previo de generación">
      {loading && (
        <div className="py-8">
          <Spinner className="" />
          <p className="text-center text-sm text-gray-500 mt-3">
            Calculando el plan de PDFs…
          </p>
        </div>
      )}

      {!loading && error && (
        <div className="space-y-4">
          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            No se pudo calcular el resumen previo: {error}
          </div>
          <p className="text-sm text-gray-600">
            Puedes volver a intentarlo o generar directamente sin resumen (no se
            mostrarán los trabajos, huecos ni avisos antes de crear el job).
          </p>
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="secondary" onClick={onBack} disabled={creating}>
              Volver
            </Button>
            <Button onClick={onGenerateAnyway} disabled={creating}>
              Generar sin resumen
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && preview && (
        <div className="space-y-4">
          {/* Plan-wide totals */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="text-lg font-bold text-gray-900">{preview.totals.cards}</div>
              <div className="text-xs text-gray-500">cartas</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className={`text-lg font-bold ${preview.totals.blanks > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {preview.totals.blanks}
              </div>
              <div className="text-xs text-gray-500">huecos</div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded p-2">
              <div className="text-lg font-bold text-gray-900">
                {preview.totals.front_pages + preview.totals.back_pages}
              </div>
              <div className="text-xs text-gray-500">páginas</div>
            </div>
          </div>

          {/* One row per resulting PDF job */}
          <ul className="space-y-2">
            {preview.jobs.map((job) => (
              <li
                key={job.name}
                className="border border-gray-200 rounded-lg p-3 space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900 truncate">📄 {job.name}</span>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold ${
                      job.merged
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {job.merged ? 'FUSIONADO' : 'INDEPENDIENTE'}
                  </span>
                </div>
                <div className="text-xs text-gray-500">
                  {job.list_names.join(' + ')} · {job.tcg.toUpperCase()}
                </div>
                <div className="text-sm text-gray-700 flex flex-wrap gap-x-4">
                  <span>
                    🃏 {job.total_cards} cartas
                    {job.extra_locals > 0 && ` (+${job.extra_locals} locales)`}
                  </span>
                  <span className={job.blanks > 0 ? 'text-amber-600 font-medium' : ''}>
                      {job.blanks > 0 ? `🕳️ ${job.blanks} hueco(s)` : '✓ sin huecos'}
                  </span>
                  <span>
                    📖 {job.front_pages} frontales
                    {!preview.fronts_only && ` + ${job.back_pages} traseras`}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {/* Plan warnings */}
          {preview.warnings.length > 0 && (
            <ul className="space-y-2">
              {preview.warnings.map((w) => (
                <li
                  key={`${w.code}-${w.message}`}
                  className={`border rounded p-2 text-sm ${SEVERITY_CLASS[w.severity] ?? SEVERITY_CLASS.info}`}
                >
                  {SEVERITY_ICON[w.severity] ?? 'ℹ️'} {w.message}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 justify-end pt-1">
            <Button variant="secondary" onClick={onBack} disabled={creating}>
              Volver
            </Button>
            <Button onClick={onConfirm} disabled={creating}>
              {creating ? 'Creando job…' : 'Confirmar y generar'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
